// Shared plan utilities — used by ProjectDetailPage and GanttChartPage.
//
// NF-S4 — `addBD`, `bdBetween`, `normalizeToBusinessDay`, `recalcDates`
// take an optional `holidays` parameter: a `Set<string>` of `YYYY-MM-DD`
// ISO dates (the user's LOCAL timezone, matching how the calendar UI
// formats them). When the set is undefined or empty, behaviour matches
// the pre-NF-S4 weekend-only semantics — every existing caller that
// doesn't pass it continues to work.

export const WK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const fmtD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${WK[dt.getDay()]} ${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
};

export const parseDmy = (s) => {
  if (!s) return null;
  const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return null;
  let y = +m[3]; if (y < 100) y += 2000;
  return new Date(y, +m[2] - 1, +m[1]);
};

// DATE-TZ-FIX-S1 — tz-agnostic 'yyyy-MM-dd' for the LOCAL calendar date
// of a JS Date. Replaces `.toISOString()` on planned-date writes, which
// in UTC+ zones serialise local midnight as the PREVIOUS evening UTC and
// store the shifted instant in datetime2.
export const toLocalIsoDate = (value) => {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) {
    return typeof value === 'string' ? value.slice(0, 10) : null;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// DATE-TZ-FIX-S1 — parse a stored planned date into a LOCAL Date. The
// bare-bones `new Date('2026-07-06')` is interpreted as UTC midnight by
// the JS spec; in UTC+ zones that re-shifts on display. We take just
// the date part and construct a local Date.
export const parseDateLocal = (value) => {
  if (!value) return null;
  if (value instanceof Date) return new Date(value);  // clone
  const s = String(value);
  const datePart = s.indexOf('T') >= 0 ? s.slice(0, s.indexOf('T')) : s.slice(0, 10);
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return new Date(+m[1], +m[2] - 1, +m[3]);
};

// NF-S4 — single predicate every business-day helper relies on. Weekends
// are always non-working; the optional `holidays` set adds whatever
// `getHolidays()` returned this session.
const isoLocal = (d) => d.toLocaleDateString('sv-SE');
const isNonWorking = (d, holidays) => {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return true;
  if (!holidays || holidays.size === 0) return false;
  return holidays.has(isoLocal(d));
};

export const addBD = (d, n, holidays) => {
  const r = new Date(d); let a = 0;
  if (n === 0) return r;
  while (a < n) {
    r.setDate(r.getDate() + 1);
    if (!isNonWorking(r, holidays)) a++;
  }
  return r;
};

export const bdBetween = (a, b, holidays) => {
  let c = 0; const d = new Date(a);
  while (d < b) {
    d.setDate(d.getDate() + 1);
    if (!isNonWorking(d, holidays)) c++;
  }
  return c;
};

// Build presentation list with isSummaryDerived flag.
// WBS-RETIRE — pre-retirement the check filtered out WBS
// descendants ("costing-summary iff has a non-WBS descendant"); with
// no WBS rows the condition reduces to "has any descendant".
export const buildRows = (tasks) => {
  const rows = tasks.map((t, i) => ({ ...t, idx: i }));
  for (let i = 0; i < rows.length; i++) {
    const myLevel = rows[i].outlineLevel;
    let hasDescendant = false;
    for (let j = i + 1; j < rows.length; j++) {
      if (rows[j].outlineLevel <= myLevel) break;
      hasDescendant = true;
      break;
    }
    rows[i].isSummaryDerived = hasDescendant;
  }
  return rows;
};

// Returns array of indices of descendant rows
export const descendants = (rows, i) => {
  const lv = rows[i].outlineLevel; const r = [];
  for (let j = i + 1; j < rows.length; j++) {
    if (rows[j].outlineLevel <= lv) break;
    r.push(j);
  }
  return r;
};

// Compute client-side dates (_start, _finish) for all rows
export const recalcDates = (rows, holidays) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Pass 1 — leaves only. AUTO tasks depend on their predecessor's _finish,
  // which is always earlier in the array (lower sortOrder), so forward order
  // is correct for the chain.
  // WBS-RETIRE — the WBS pass-through branch is gone with the mode
  // (no row has taskMode === 'WBS' anymore).
  for (let i = 0; i < rows.length; i++) {
    const t = rows[i];
    if (t.isSummaryDerived) continue;
    if ((t.taskMode || 'AUTO') === 'AUTO') {
      // DP-S4: resolve the effective predecessor — own first, else walk up
      // ancestor summaries (earlier rows with a lower outlineLevel).
      let effectivePredSortOrder = t.predecessorSortOrder || null;
      if (!effectivePredSortOrder) {
        for (let j = i - 1; j >= 0; j--) {
          if (rows[j].outlineLevel >= t.outlineLevel) continue;
          if (!rows[j].isSummaryDerived) break; // ancestor is a leaf — stop
          if (rows[j].predecessorSortOrder) {
            effectivePredSortOrder = rows[j].predecessorSortOrder;
            break;
          }
        }
      }

      let start = null;
      if (effectivePredSortOrder) {
        const predIdx = rows.findIndex((r) => r.sortOrder === effectivePredSortOrder);
        if (predIdx >= 0) {
          // Effective finish: if the predecessor is a summary, take the MAX
          // finish of its non-summary descendants (already set in Pass 1).
          let predFinish = null;
          if (rows[predIdx].isSummaryDerived) {
            for (const j of descendants(rows, predIdx)) {
              if (!rows[j].isSummaryDerived && rows[j]._finish) {
                if (!predFinish || rows[j]._finish > predFinish) predFinish = rows[j]._finish;
              }
            }
          } else {
            predFinish = rows[predIdx]._finish;
          }
          if (predFinish) {
            // NF-S4: skip weekends AND holidays when walking from the
            // predecessor's finish to the new task's start.
            const s = new Date(predFinish); s.setDate(s.getDate() + 1);
            while (isNonWorking(s, holidays)) s.setDate(s.getDate() + 1);
            start = s;
          }
        }
      }
      if (!start) start = parseDateLocal(t.plannedStartDate) || new Date(today);
      t._start = start;
      t._finish = (t.durationDays || 0) === 0 ? new Date(start) : addBD(start, t.durationDays - 1, holidays);
    } else {
      t._start = parseDateLocal(t.plannedStartDate) || new Date(today);
      t._finish = parseDateLocal(t.plannedEndDate) || addBD(t._start, (t.durationDays || 1) - 1, holidays);
    }
  }

  // Pass 2 — summaries. By now every leaf has _start / _finish, so
  // descendants produce real mn / mx. Nested summaries work either
  // order (each only considers leaves via the !isSummaryDerived
  // filter).
  // WBS-RETIRE — the "skip WBS descendants" filter is gone with the
  // mode; every descendant leaf contributes to the summary band.
  for (let i = 0; i < rows.length; i++) {
    const t = rows[i];
    if (!t.isSummaryDerived) continue;
    const ds = descendants(rows, i);
    let mn = null, mx = null;
    for (const j of ds) {
      if (!rows[j].isSummaryDerived && rows[j]._start && rows[j]._finish) {
        if (!mn || rows[j]._start < mn) mn = rows[j]._start;
        if (!mx || rows[j]._finish > mx) mx = rows[j]._finish;
      }
    }
    t._start = mn; t._finish = mx;
    t._spanDays = mn && mx ? bdBetween(mn, mx, holidays) + 1 : (t.durationDays || 0);
  }

  return rows;
};

// Normalize a Date to the nearest business day in the given direction.
// 'forward' → Saturday/Sunday → next Monday.
// 'backward' → Saturday/Sunday → previous Friday.
// NF-S4: holidays count as non-working and are skipped the same way.
export const normalizeToBusinessDay = (d, direction = 'forward', holidays) => {
  const r = new Date(d);
  const step = direction === 'backward' ? -1 : 1;
  while (isNonWorking(r, holidays)) {
    r.setDate(r.getDate() + step);
  }
  return r;
};
